import React from "react";
import getAvatarUrl from "../../../utils/get-avatar-url";
import BookingMeetingLink from "./BookingMeetingLink";
import { bookingStatusLabel, bookingStatusTone } from "./booking-status";
import { meetingProviderLabel } from "./meeting-provider-label";
import { getPtCapacitySummary } from "./pt-session-slots";
import { formatDailyWeekdays } from "./recurring-weekday-encoding";
import { normalizeBookingCutoff } from "./scheduling-limit-period";

const KIND_LABELS = {
  class: "Classes",
  pt: "PT Session",
  facility: "Facility",
  consult: "Consult",
  blackout_summary: "Blocked Time",
};

const RECURRENCE_LABELS = {
  one_time: "One-time",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  custom: "Custom",
};

function formatPanelDate(date, tz) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  const dayOpts = { day: "numeric" };
  const monthOpts = { month: "short" };
  const yearOpts = { year: "numeric" };
  const weekdayOpts = { weekday: "short" };
  if (tz) {
    dayOpts.timeZone = tz;
    monthOpts.timeZone = tz;
    yearOpts.timeZone = tz;
    weekdayOpts.timeZone = tz;
  }
  const day = date.toLocaleDateString("en-US", dayOpts);
  const month = date.toLocaleDateString("en-US", monthOpts);
  const year = date.toLocaleDateString("en-US", yearOpts);
  const weekday = date.toLocaleDateString("en-US", weekdayOpts);
  return `${month} ${day}, ${year} (${weekday})`;
}

function formatPanelTimeRange(start, end, tz) {
  if (!(start instanceof Date)) return "—";
  const opts = { hour: "numeric", minute: "2-digit", hour12: true };
  if (tz) opts.timeZone = tz;
  const a = start
    .toLocaleTimeString("en-US", opts)
    .toLowerCase()
    .replace(" ", "");
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return a;
  const b = end
    .toLocaleTimeString("en-US", opts)
    .toLowerCase()
    .replace(" ", "");
  return `${a} - ${b}`;
}

function formatDurationMins(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const m = Number(minutes);
  if (m < 60) return `${m} mins`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function formatBookingCutoff(hours) {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) return "No cutoff";
  if (value < 1) {
    const mins = Math.round(value * 60);
    return `${mins} minutes before start`;
  }
  return `${value} ${value === 1 ? "hour" : "hours"} before start`;
}

function eventStatusBadge(start, end) {
  if (!(start instanceof Date))
    return { label: "Scheduled", tone: "scheduled" };
  const now = Date.now();
  if (start.getTime() > now) return { label: "Upcoming", tone: "upcoming" };
  const finish =
    end instanceof Date && !Number.isNaN(end.getTime()) ? end : start;
  if (finish.getTime() > now) return { label: "Upcoming", tone: "upcoming" };
  return { label: "Completed", tone: "completed" };
}

function extractCoaches(raw, theme) {
  if (!raw) return [];
  const list = Array.isArray(raw.coaches) ? raw.coaches : [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(c.name ?? "").trim() || "Coach",
      role: String(c.role ?? "").trim() || "Coach",
      avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
    });
  }
  return out;
}

const SectionRow = ({ icon, label, children }) => (
  <div className="event-details-panel-row">
    <span className="event-details-panel-row-label">
      <img src={icon} alt="" aria-hidden />
      {label}
    </span>
    <span className="event-details-panel-row-value">{children}</span>
  </div>
);

const EventDetailsPanel = ({
  event,
  activeUser,
  theme,
  onClose,
  onEdit,
  onBlockTime,
  onBlockThisEvent,
  onUnblockEvent,
  onMarkAttendance,
  members,
  membersLoading,
  canManage = true,
}) => {
  const [tab, setTab] = React.useState("details");
  const [search, setSearch] = React.useState("");
  const [blockMenuOpen, setBlockMenuOpen] = React.useState(false);
  const blockMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (!blockMenuOpen) return;
    const handler = (e) => {
      if (blockMenuRef.current && !blockMenuRef.current.contains(e.target)) {
        setBlockMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [blockMenuOpen]);

  React.useEffect(() => {
    if (event) {
      setTab("details");
      setSearch("");
    }
  }, [event?.key]);

  if (!event) return null;

  const raw = event.raw ?? {};
  const kindLabel = KIND_LABELS[event.kind] ?? "Event";
  const status = eventStatusBadge(event.start, event.end);
  const recurrenceBase =
    RECURRENCE_LABELS[raw.recurrence_type ?? raw.schedule_type] ??
    raw.recurrence_type ??
    raw.schedule_type ??
    "—";
  const recurrence =
    raw.recurrence_type === "daily"
      ? `${recurrenceBase} ${formatDailyWeekdays(raw.recurrence_weekdays)}`.trim()
      : recurrenceBase;
  const location =
    raw.location_type === "online"
      ? `Online · ${meetingProviderLabel(raw.meeting_provider)}`
      : raw.location?.toString().trim() || "—";
  const capacity =
    raw.capacity != null
      ? `${raw.capacity} ${Number(raw.capacity) === 1 ? "seat" : "seats"}`
      : "—";
  const coaches = extractCoaches(raw, theme);
  const showPtSlot = event.kind === "pt";
  const description = String(raw.description ?? "").trim();
  const hasThumbnailImage =
    typeof raw.thumbnail_url === "string" && raw.thumbnail_url.length > 0;
  const thumbnail = hasThumbnailImage
    ? raw.thumbnail_url
    : event.kind === "facility"
      ? "/img/new-icons/facilities-icon.svg"
      : event.kind === "pt"
        ? "/img/new-icons/pt-session-icon.svg"
        : event.kind === "consult"
          ? "/img/new-icons/person-walking-solid.svg"
          : "/img/new-icons/classes-icon.svg";
  const thumbIsConsultIcon = !hasThumbnailImage && event.kind === "consult";

  const memberCount = Array.isArray(members) ? members.length : 0;
  const ptCapacity =
    event.kind === "pt" ? getPtCapacitySummary(raw, memberCount) : null;

  return (
    <>
      <button
        type="button"
        className="event-details-panel-overlay"
        aria-label="Close event details"
        onClick={onClose}
      />
      <aside
        className="event-details-panel"
        role="dialog"
        aria-label="Event details"
      >
        <header className="event-details-panel-header">
          <div className="event-details-panel-header-title">
            <h2>Event Details</h2>
            <span
              className={`event-details-panel-status event-details-panel-status--${status.tone}`}
            >
              {status.label}
            </span>
          </div>
          <button
            type="button"
            className="event-details-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            <img src="/img/new-icons/add-square-scheduling.svg" alt="" />
          </button>
        </header>

        {event.kind !== "blackout_summary" && (
          <nav className="event-details-panel-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "details"}
              className={
                tab === "details"
                  ? "event-details-panel-tab is-active"
                  : "event-details-panel-tab"
              }
              onClick={() => setTab("details")}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "members"}
              className={
                tab === "members"
                  ? "event-details-panel-tab is-active"
                  : "event-details-panel-tab"
              }
              onClick={() => setTab("members")}
            >
              Members ({memberCount})
            </button>
          </nav>
        )}

        <div className="event-details-panel-body">
          {event.kind === "blackout_summary" ? (
            <div className="event-details-panel-rows">
              <SectionRow
                icon="/img/new-icons/vuesax-linear-calendar-schedule.svg"
                label="Date"
              >
                {formatPanelDate(event.start, event.raw?.timezone)}
              </SectionRow>
              <SectionRow
                icon="/img/new-icons/vuesax-linear-clock.svg"
                label="Time"
              >
                {formatPanelTimeRange(
                  event.start,
                  event.end,
                  event.raw?.timezone
                )}
              </SectionRow>
              {(raw.reason || raw.description) && (
                <SectionRow
                  icon="/img/new-icons/vuesax-linear-info-circle.svg"
                  label="Reason"
                >
                  {raw.reason || raw.description}
                </SectionRow>
              )}
            </div>
          ) : tab === "details" ? (
            <>
              <div className="event-details-panel-hero">
                <div className="event-details-panel-thumb">
                  <img
                    src={thumbnail}
                    alt=""
                    className={
                      thumbIsConsultIcon
                        ? "event-details-panel-thumb-img--consult-icon"
                        : undefined
                    }
                  />
                </div>
                <div className="event-details-panel-hero-text">
                  <h3 className="event-details-panel-title">{event.title}</h3>
                  {description && (
                    <p className="event-details-panel-description">
                      {description}
                    </p>
                  )}
                </div>
              </div>

              <div className="event-details-panel-rows">
                <SectionRow
                  icon="/img/new-icons/type-scheduling.svg"
                  label="Type"
                >
                  <span
                    className={`event-details-panel-type event-details-panel-type--${event.kind}`}
                  >
                    {kindLabel}
                  </span>
                </SectionRow>
                <SectionRow
                  icon="/img/new-icons/vuesax-linear-calendar-schedule.svg"
                  label="Date"
                >
                  {formatPanelDate(event.start, event.raw?.timezone)}
                </SectionRow>
                <SectionRow
                  icon="/img/new-icons/vuesax-linear-clock.svg"
                  label="Time"
                >
                  {formatPanelTimeRange(
                    event.start,
                    event.end,
                    event.raw?.timezone
                  )}
                </SectionRow>
                <SectionRow
                  icon="/img/new-icons/duration-scheduling.svg"
                  label="Duration"
                >
                  {formatDurationMins(raw.duration_minutes)}
                </SectionRow>
                {normalizeBookingCutoff(raw.booking_cutoff) != null && (
                  <SectionRow
                    icon="/img/new-icons/vuesax-linear-clock.svg"
                    label="Booking cutoff"
                  >
                    {formatBookingCutoff(raw.booking_cutoff)}
                  </SectionRow>
                )}
                <SectionRow
                  icon="/img/new-icons/reccurence-scheduling.svg"
                  label="Recurrence"
                >
                  {recurrence}
                </SectionRow>
                <SectionRow
                  icon="/img/new-icons/location-scheduling.svg"
                  label="Location"
                >
                  {location}
                </SectionRow>
                {event.kind === "class" && raw.capacity != null && (
                  <SectionRow
                    icon="/img/new-icons/classes-icon.svg"
                    label="Capacity"
                  >
                    {capacity}
                  </SectionRow>
                )}
                {event.kind === "pt" && ptCapacity?.capacity != null && (
                  <SectionRow
                    icon="/img/new-icons/classes-icon.svg"
                    label="Capacity"
                  >
                    {`${ptCapacity.booked}/${ptCapacity.capacity} booked · ${ptCapacity.remaining} left`}
                  </SectionRow>
                )}
                {coaches.length > 0 && (
                  <div className="event-details-panel-row event-details-panel-row--coaches">
                    <span className="event-details-panel-row-label">
                      <img
                        src="/img/new-icons/coach-scheduling.svg"
                        alt=""
                        aria-hidden
                      />
                      Coaches
                    </span>
                    <ul className="event-details-panel-coaches">
                      {coaches.map((c) => (
                        <li key={c.id} className="event-details-panel-coach">
                          <img
                            src={c.avatarUrl}
                            alt=""
                            className="event-details-panel-coach-avatar"
                          />
                          <span className="event-details-panel-coach-text">
                            <span className="event-details-panel-coach-name">
                              {c.name}
                            </span>
                            <span className="event-details-panel-coach-role">
                              {c.role}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="event-details-panel-members">
              {(() => {
                const attendedCount = Array.isArray(members)
                  ? members.filter(
                      (m) => bookingStatusTone(m.statusLabel) === "attended"
                    ).length
                  : 0;
                const absentCount = Array.isArray(members)
                  ? members.filter(
                      (m) => bookingStatusTone(m.statusLabel) === "absent"
                    ).length
                  : 0;
                const isUpcoming = status.tone !== "completed";
                const capacityNum =
                  raw.capacity != null ? Number(raw.capacity) : null;
                const seatsLeft =
                  capacityNum != null && Number.isFinite(capacityNum)
                    ? Math.max(0, capacityNum - memberCount)
                    : null;
                if (isUpcoming) {
                  return (
                    <div className="event-details-panel-members-capacity">
                      <div className="event-details-panel-members-capacity-label">
                        <img
                          src="/img/new-icons/classes-icon.svg"
                          alt=""
                          aria-hidden
                        />
                        <span>Capacity</span>
                      </div>
                      <span className="event-details-panel-members-capacity-booked">
                        {event.kind === "class" && capacityNum != null
                          ? `${memberCount}/${capacityNum} Booked`
                          : `${memberCount} ${memberCount === 1 ? "booking" : "bookings"}`}
                      </span>
                      {(event.kind === "class" || event.kind === "pt") &&
                        (event.kind === "pt"
                          ? ptCapacity?.remaining != null
                          : seatsLeft != null) && (
                          <span className="event-details-panel-members-seats-left">
                            <img
                              src="/img/new-icons/info-circle.svg"
                              alt=""
                              aria-hidden
                            />
                            {event.kind === "pt"
                              ? ptCapacity.remaining
                              : seatsLeft}{" "}
                            left
                          </span>
                        )}
                    </div>
                  );
                }
                return (
                  <div className="event-details-panel-members-stats">
                    {(raw.capacity != null || ptCapacity?.capacity != null) && (
                      <div className="event-details-panel-members-stat">
                        <img
                          src="/img/new-icons/classes-icon.svg"
                          alt=""
                          aria-hidden
                        />
                        <div className="event-details-panel-members-stat-text">
                          <span className="event-details-panel-members-stat-label">
                            Capacity
                          </span>
                          <span className="event-details-panel-members-stat-value">
                            {event.kind === "pt"
                              ? `${ptCapacity.booked}/${ptCapacity.capacity} booked · ${ptCapacity.remaining} left`
                              : event.kind === "class"
                                ? `${memberCount}/${Number(raw.capacity)} booked`
                                : `${memberCount} ${memberCount === 1 ? "booking" : "bookings"}`}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="event-details-panel-members-stat">
                      <img
                        src="/img/new-icons/attended-scheduling.svg"
                        alt=""
                        aria-hidden
                      />
                      <div className="event-details-panel-members-stat-text">
                        <span className="event-details-panel-members-stat-label">
                          Attended
                        </span>
                        <span className="event-details-panel-members-stat-value">
                          {`${attendedCount} ${attendedCount === 1 ? "member" : "members"}`}
                        </span>
                      </div>
                    </div>
                    <div className="event-details-panel-members-stat">
                      <img
                        src="/img/new-icons/absent-scheduling.svg"
                        alt=""
                        aria-hidden
                      />
                      <div className="event-details-panel-members-stat-text">
                        <span className="event-details-panel-members-stat-label">
                          No show
                        </span>
                        <span className="event-details-panel-members-stat-value">
                          {`${absentCount} ${absentCount === 1 ? "member" : "members"}`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="event-details-panel-member-search-wrap">
                <input
                  type="text"
                  className="event-details-panel-member-search"
                  placeholder="Search members.."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="event-details-panel-member-search-clear"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    <img src="/img/new-icons/close-search.svg" alt="" />
                  </button>
                )}
              </div>
              {membersLoading ? (
                <p className="event-details-panel-empty">Loading members…</p>
              ) : memberCount === 0 ? (
                <p className="event-details-panel-empty">
                  No members booked yet.
                </p>
              ) : (
                (() => {
                  const q = search.trim().toLowerCase();
                  const filtered = q
                    ? members.filter(
                        (m) =>
                          m.name.toLowerCase().includes(q) ||
                          m.email?.toLowerCase().includes(q)
                      )
                    : members;
                  return filtered.length === 0 ? (
                    <p className="event-details-panel-empty">
                      No members match your search.
                    </p>
                  ) : (
                    <ul className="event-details-panel-member-list">
                      {filtered.map((m) => {
                        const tone = bookingStatusTone(m.statusLabel);
                        return (
                          <li key={m.id} className="event-details-panel-member">
                            <img
                              src={m.avatarUrl}
                              alt=""
                              className="event-details-panel-member-avatar"
                            />
                            <span className="event-details-panel-member-text">
                              <span className="event-details-panel-member-name">
                                {m.name}
                              </span>
                              {m.email && (
                                <span className="event-details-panel-member-email">
                                  {m.email}
                                </span>
                              )}
                              {showPtSlot && m.bookedAt && (
                                <span className="event-details-panel-member-booked-at">
                                  {m.bookedAt}
                                </span>
                              )}
                              {event.kind === "consult" &&
                                status.tone !== "completed" && (
                                  <BookingMeetingLink
                                    url={m.meetingJoinUrl}
                                    provider={m.meetingProvider}
                                    status={m.meetingStatus}
                                  />
                                )}
                            </span>
                            <span className="event-details-panel-member-meta">
                              <span className="event-details-panel-member-book-type">
                                {m.isGuest ? "Guest" : (m.bookType ?? "—")}
                              </span>
                              <span
                                className={`event-details-panel-member-status event-details-panel-member-status--${tone}`}
                              >
                                {bookingStatusLabel(m.statusLabel)}
                              </span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()
              )}
            </div>
          )}
        </div>

        {canManage &&
          event.kind !== "blackout_summary" &&
          !event.singleEventBlock &&
          status.tone === "completed" &&
          tab === "members" && (
            <footer className="event-details-panel-footer event-details-panel-footer--mark-attendance">
              <button
                type="button"
                className="event-details-panel-mark-attendance-btn"
                onClick={() => onMarkAttendance?.(event)}
              >
                <img
                  src="/img/new-icons/attendance-scheduling.svg"
                  alt=""
                  aria-hidden
                />
                Mark Attendance
              </button>
            </footer>
          )}

        {canManage &&
          (event.singleEventBlock || event.kind === "blackout_summary") && (
            <footer className="event-details-panel-footer">
              <button
                type="button"
                className="event-details-panel-footer-btn event-details-panel-footer-btn--edit"
                onClick={() => {
                  onClose?.();
                  onUnblockEvent?.(event);
                }}
              >
                <img
                  src="/img/new-icons/cancel-invite.svg"
                  alt=""
                  aria-hidden
                />
                Unblock
              </button>
            </footer>
          )}

        {canManage &&
          event.kind !== "blackout_summary" &&
          !event.singleEventBlock &&
          status.tone !== "completed" && (
            <footer className="event-details-panel-footer">
              <button
                type="button"
                className="event-details-panel-footer-btn event-details-panel-footer-btn--edit"
                onClick={() => {
                  onClose?.();
                  onEdit?.(event);
                }}
              >
                <img src="/img/new-icons/edit-pencil.svg" alt="" aria-hidden />
                Edit
              </button>
              <div
                className="event-details-panel-block-wrap"
                ref={blockMenuRef}
              >
                {blockMenuOpen && (
                  <div className="event-details-panel-block-menu">
                    <button
                      type="button"
                      className="event-details-panel-block-menu-item"
                      onClick={() => {
                        setBlockMenuOpen(false);
                        onBlockThisEvent?.(event);
                      }}
                    >
                      Block this event only
                    </button>
                    <hr className="event-details-panel-block-menu-divider" />
                    <button
                      type="button"
                      className="event-details-panel-block-menu-item"
                      onClick={() => {
                        setBlockMenuOpen(false);
                        onBlockTime?.(event);
                      }}
                    >
                      Block upcoming events
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="event-details-panel-footer-btn event-details-panel-footer-btn--block"
                  onClick={() => setBlockMenuOpen((o) => !o)}
                >
                  <img src="/img/new-icons/slash.svg" alt="" aria-hidden />
                  Block Time
                  <img
                    src="/img/new-icons/chevron-down.svg"
                    alt=""
                    aria-hidden
                    className={`event-details-panel-block-caret${blockMenuOpen ? " is-open" : ""}`}
                  />
                </button>
              </div>
            </footer>
          )}
      </aside>
    </>
  );
};

export default EventDetailsPanel;
